/** Версии промпта героини: «Составить» (композер за деньги), выбор версии, правка руками → новая версия. */
import { useMutation } from "@tanstack/react-query";
import { Loader2, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";

import { Btn, ErrorLine, Hint, Panel, selectClass } from "@/components/project/ui";
import { when } from "@/lib/media";
import { addPrompt, composePrompt, type PromptVersion } from "@/lib/persona";
import { errorText, usd } from "@/lib/project";

const ORIGIN_LABEL: Record<PromptVersion["origin"], string> = {
  composer: "композер",
  manual: "руками",
  feedback: "из отзыва",
};

const promptLabel = (p: PromptVersion) =>
  `v${p.version} · ${ORIGIN_LABEL[p.origin]} · ${p.author_name} · ${when(p.date_create)}`;

export function PromptPanel({
  pid,
  prompts,
  selectedId,
  onSelect,
  onComposed,
}: {
  pid: number;
  prompts: PromptVersion[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  onComposed: () => void;
}) {
  const selected = prompts.find((p) => p.id === selectedId) ?? null;
  const [text, setText] = useState(selected?.text ?? "");
  // Смена версии (или её текста после перечитывания) подменяет черновик.
  useEffect(() => setText(selected?.text ?? ""), [selected?.id, selected?.text]);

  const compose = useMutation({
    mutationFn: () => composePrompt(pid),
    onSuccess: (r) => {
      onSelect(r.prompt.id);
      onComposed();
    },
  });
  const add = useMutation({
    mutationFn: () => addPrompt(pid, text),
    onSuccess: (r) => {
      onSelect(r.prompt.id);
      onComposed();
    },
  });
  const changed = selected !== null ? text !== selected.text : text.trim() !== "";

  return (
    <Panel title="Промпт" aside={compose.data ? `стоило ${usd(compose.data.cost)}` : undefined} annot="промпт героини">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <Btn onClick={() => compose.mutate()} disabled={compose.isPending}>
          {compose.isPending ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
          Составить промпт
        </Btn>
        {prompts.length > 0 ? (
          <select
            className={`${selectClass} min-w-0 flex-1`}
            value={selected?.id ?? ""}
            onChange={(e) => onSelect(Number(e.target.value))}
          >
            {prompts.map((p) => (
              <option key={p.id} value={p.id}>
                {promptLabel(p)}
              </option>
            ))}
          </select>
        ) : null}
      </div>
      <ErrorLine text={compose.isError ? errorText(compose.error) : null} className="mb-2" />
      {prompts.length === 0 ? <Hint className="mb-2">Версий ещё нет: составьте из брифа или впишите руками.</Hint> : null}
      <textarea
        value={text}
        rows={9}
        onChange={(e) => setText(e.target.value)}
        className="w-full resize-y rounded-[var(--radius-ui)] border border-border bg-bg px-2.5 py-2 font-mono text-[12px] text-fg outline-none focus:border-accent"
      />
      <div className="mt-2 flex items-center gap-2">
        <Btn tone="primary" onClick={() => add.mutate()} disabled={!changed || add.isPending || text.trim() === ""}>
          Сохранить как версию
        </Btn>
        <Hint>Правка не меняет старую версию — это новая.</Hint>
      </div>
      <ErrorLine text={add.isError ? errorText(add.error) : null} className="mt-2" />
    </Panel>
  );
}
